import {useEffect, useRef} from "react";
import {HubConnection, HubConnectionBuilder, LogLevel} from "@microsoft/signalr";
import {ApiUrl_SignalR_CommentHub} from "../Endpoints";
import {CommentNotificationDTO} from "./signalr.models";
import {JwtUtils} from "../auth/JwtUtils";
import getToken = JwtUtils.getToken;
import {useAuthenticationContext} from "../auth/AuthenticationContext";

export default function useCommentSignalRConnection() {
    const connectionRef = useRef<HubConnection | null>(null);
    const connectionIdRef = useRef<string | null>(null);
    const commentHubUrl: string = ApiUrl_SignalR_CommentHub;
    const progressEndpointName: string = "ReceiveNewComment";
    const {comments, rootModel} = useAuthenticationContext();
    const commentsRef = useRef(comments);
    commentsRef.current = comments;

    useEffect(() => {
        if (!rootModel?.CurrentUserProfile.ShowNewCommentsPopup)
            return;
        startSignalConnection();
        return () => {
            connectionRef.current?.stop()
                .catch((err) => console.error("Error stopping comment hub SignalR connection:", err));
            connectionRef.current = null;
        };
    }, [rootModel?.CurrentUserProfile.LabId, rootModel?.CurrentUserProfile.DivisionId])

    function playChime() {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();

        function playNote(frequency: number, startDelay: number, peak: number, duration: number) {
            const osc = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            const t = audioCtx.currentTime + startDelay;

            osc.type = 'sine';
            osc.frequency.setValueAtTime(frequency, t);

            gain.gain.setValueAtTime(0, t);
            gain.gain.linearRampToValueAtTime(peak, t + 0.012);
            gain.gain.exponentialRampToValueAtTime(peak * 0.4, t + 0.08);
            gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

            osc.connect(gain).connect(audioCtx.destination);
            osc.start(t);
            osc.stop(t + duration);
        }

        // Gentle ascending three-note chime: E5 → G5 → B5
        playNote(659, 0,    0.22, 0.7);
        playNote(784, 0.13, 0.18, 0.7);
        playNote(988, 0.26, 0.15, 0.8);

        // Close context after all notes finish to avoid AudioContext limit (~6 per browser)
        setTimeout(() => audioCtx.close(), 1500);
    }

    function startSignalConnection() {
        const connection = new HubConnectionBuilder()
            .withUrl(commentHubUrl, {
                withCredentials: false,
                accessTokenFactory: () => getToken() || "",
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Warning)
            .build();

        connectionRef.current = connection;

        connection.on(progressEndpointName, (comment: CommentNotificationDTO) => {
            if (commentsRef.current.append(comment))
                playChime();
        });

        connection
            .start()
            .then(() => {
                connectionIdRef.current = connection.connectionId;
            })
            .catch((err) => console.error("Comment hub SignalR Connection Error:", err));

    }


    return {
        commentData: {
            connectionId: connectionIdRef.current,
            comments: {
                get: comments.get,
                //set: setComments // Allows setting comments manually if needed
            }
        }
    };
}

